import { useTranslations } from 'next-intl';
import { useMedia } from 'react-use';
import {
  ContentLayout,
  MinimalPageContent,
} from '@silvertours/common-landingpages-view';
import { Runtime } from '@silvertours/front-entities';
import {
  StageLegacy,
  ContentArticle,
  Author,
  AuthorInTitle,
  GuideStage,
  SearchFormLegacy,
} from '@silvertours/front-features';
import { Theme } from '@silvertours/front-shared';

import { Body } from '../body';

/**
 * This is a page template that only renders the introduction and content modules.
 * Create for legal but can be used for rendering other pages where we don't
 * want to render legacy elements. To use the template, add necessary slug
 * or domainKeys in i18n/json/app/pageContent/[LOCALE].ts
 */
const MinimalPage = ({
  content: {
    article,
    introduction,
    stage,
    author,
    guidePageType,
    guideStage,
    textContent: { primaryHeadline },
  },
  ...commonProps
}: ContentLayout<MinimalPageContent>) => {
  const t = useTranslations('features');
  const translations = t.raw('article' as any);

  const { isRatgeberPage } = Runtime.usePath();
  const isXl = useMedia(`(min-width: ${Theme.getBreakpoint('xl')}px)`, false);
  const searchMask = isRatgeberPage && isXl ? 'thin' : 'none';
  const variant = { kind: 'minimal', searchMask } as const;

  return (
    <Body
      variant={variant}
      title={primaryHeadline}
      author={
        author && guidePageType !== 'author' && <AuthorInTitle {...author} />
      }
      bodyTop={
        searchMask === 'thin' ? (
          <SearchFormLegacy.SearchForm displayMode="thin" isWide />
        ) : (
          stage && (
            <StageLegacy.Stage
              content={stage.content}
              headline={primaryHeadline}
              variant={variant}
            />
          )
        )
      }
      searchData={stage?.content?.search}
      content={({ container, containerModule, contentModule }) => [
        containerModule(
          'guideStage',
          guideStage && <GuideStage image={guideStage} />,
          !!guideStage,
        ),
        containerModule(
          'introduction-content',
          <ContentArticle
            modules={introduction?.segments ?? []}
            translations={translations}
          />,
          !!introduction?.segments,
        ),
        container('content-modules', [
          contentModule(
            'article',
            <ContentArticle
              modules={article.segments}
              translations={translations}
            />,
          ),
        ]),
        containerModule(
          'author',
          author && guidePageType !== 'author' && <Author {...author} />,
          !!author,
        ),
      ]}
      {...commonProps}
    />
  );
};

export { MinimalPage };
